/**
 * ================================================================
 * LILLBUD — Shop by Age · JS Controller
 * File: assets/lb-age.js
 * Add before </body> in theme.liquid:
 *   <script src="{{ 'lb-age.js' | asset_url }}" defer></script>
 *
 * RESPONSIBILITIES:
 *   1. Month tab click → update progress bar, milestone banner,
 *      featured products — NO page reload
 *   2. Client-side sort — NO page reload
 *   3. Infinite scroll via IntersectionObserver
 *
 * DATA SOURCE:
 *   #lb-age-data      → month → { skills[], featuredProducts[] }
 *   #lb-products-data → full product array for sort + infinite scroll
 *
 * CART:
 *   All buttons carry data-cart-widget + data-variant-id.
 *   The existing theme cart controller handles them via event
 *   delegation on document — works for both server-rendered
 *   and JS-injected cards automatically.
 * ================================================================
 */

(function () {
  'use strict';

  /* ── CONFIG ────────────────────────────────────────────────── */
  var PAGE_SIZE     = 8;   /* products appended per scroll */
  var SCROLL_MARGIN = 200; /* px before sentinel to trigger load */

  /* ── STATE ─────────────────────────────────────────────────── */
  var state = {
    activeMonth:      0,
    minMonth:         0,
    maxMonth:         0,
    stageName:        '',
    ageData:          {},  /* month → { skills, featuredProducts } */
    allProducts:      [],  /* current working array (sorted)       */
    originalProducts: [],  /* immutable master — sort always from here */
    visibleCount:     0,   /* seeded from server-rendered card count */
    currentSort:      'manual',
    scrollObserver:   null,
    /*
     * scrollFallback — kept on state so resetInfiniteScroll can
     * remove the exact same function reference from the scroll
     * listener. A new closure is created each time bindInfiniteScroll
     * runs, so we must store it to be able to removeEventListener it.
     */
    scrollFallback:   null,
    /*
     * batchInFlight — prevents the IntersectionObserver and the
     * scroll fallback from both firing loadNextBatch simultaneously
     * before the setTimeout resolves.
     */
    batchInFlight:    false
  };

  /* ── DOM CACHE ─────────────────────────────────────────────── */
  var dom = {};

  /* ================================================================
     INIT
  ================================================================ */
  function init() {
    /* Guard: only run on age collection pages */
    if (!document.getElementById('lb-age-header'))  return;
    if (!document.getElementById('lb-age-content')) return;

    /* Parse JSON data islands */
    var ageEl  = document.getElementById('lb-age-data');
    var prodEl = document.getElementById('lb-products-data');
    if (!ageEl || !prodEl) return;

    try {
      var ageRaw        = JSON.parse(ageEl.textContent);
      state.ageData     = ageRaw.months     || {};
      state.activeMonth = ageRaw.activeMonth || 0;
      state.minMonth    = ageRaw.minMonth    || 0;
      state.maxMonth    = ageRaw.maxMonth    || 0;
      state.stageName   = ageRaw.stageName   || '';
    } catch (e) {
      console.warn('[LB] lb-age-data parse error', e);
      return;
    }

    try {
      var products           = JSON.parse(prodEl.textContent) || [];
      state.allProducts      = products;
      state.originalProducts = products.slice(); /* immutable master copy */
    } catch (e) {
      console.warn('[LB] lb-products-data parse error', e);
      return;
    }

    /* Cache DOM references */
    dom = {
      microBar:      document.getElementById('lb-micro-bar'),
      tabs:          document.getElementById('lb-micro-tabs'),
      progressFill:  document.getElementById('lb-progress-fill'),
      bannerInner:   document.getElementById('lb-ms-banner-inner'),
      banner:        document.getElementById('lb-ms-banner'),
      featuredTitle: document.getElementById('lb-featured-title'),
      featuredWrap:  document.getElementById('lb-featured-products'),
      pgGrid:        document.getElementById('lb-pg-grid'),
      sortSelect:    document.getElementById('lb-sort-select'),
      sentinel:      document.getElementById('lb-scroll-sentinel'),
      spinner:       document.getElementById('lb-loading-spinner'),
      productCount:  document.getElementById('lb-product-count')
    };

    /*
     * Seed visibleCount from server-rendered card count.
     * This prevents duplicate cards when infinite scroll first fires.
     * JS appends starting exactly where the server left off.
     */
    var serverCards    = dom.pgGrid
      ? dom.pgGrid.querySelectorAll('.lb-pg-card').length
      : 0;
    state.visibleCount = serverCards > 0 ? serverCards : PAGE_SIZE;

    /* Sync sort select from URL */
    var urlSort = new URLSearchParams(window.location.search).get('sort_by');
    if (urlSort && dom.sortSelect) {
      state.currentSort    = urlSort;
      dom.sortSelect.value = urlSort;
    }

    /* Bind UI */
    bindTabs();
    bindSort();
    syncNavHeight();
    loadJudgeMe();

    /*
     * Sort the in-memory array (always needed so the data is ready).
     * Only wipe and re-render the DOM when a non-default sort is active
     * on page load — otherwise we trust the server-rendered cards and
     * start infinite scroll exactly where the server left off.
     */
    sortProducts(state.currentSort);

    if (state.currentSort !== 'manual') {
      state.visibleCount = Math.min(PAGE_SIZE, state.allProducts.length);
      renderGrid(false);
    }

    bindInfiniteScroll();
    bindMasterFilter();
  }

  /* ================================================================
     MONTH TABS — NO page reload
  ================================================================ */
  function bindTabs() {
    if (!dom.tabs) return;
    dom.tabs.addEventListener('click', function (e) {
      var btn = e.target.closest('.lb-mtab');
      if (!btn) return;
      var mo = parseInt(btn.getAttribute('data-month'), 10);
      if (isNaN(mo) || mo === state.activeMonth) return;
      selectMonth(mo);
    });
  }

  function selectMonth(mo) {
    state.activeMonth = mo;

    /* 1. Update tab styles */
    if (dom.tabs) {
      dom.tabs.querySelectorAll('.lb-mtab').forEach(function (t) {
        var tMo    = parseInt(t.getAttribute('data-month'), 10);
        var active = (tMo === mo);
        t.classList.toggle('lb-mtab--active', active);
        t.setAttribute('aria-pressed', active);

        var moEl  = t.querySelector('.lb-mtab__mo');
        var nowEl = t.querySelector('.lb-mtab__now');

        if (moEl) {
          var label = tMo < 12
            ? tMo + 'M'
            : (tMo / 12).toFixed(2).replace(/\.00$/, '') + 'Y';
          moEl.textContent = label + (active ? ' ✦' : '');
        }

        if (nowEl) nowEl.style.display = active ? '' : 'none';

        /* Create NOW chip if missing on newly-active tab */
        if (active && !nowEl) {
          var chip       = document.createElement('span');
          chip.className = 'lb-mtab__now';
          chip.textContent = 'NOW';
          t.appendChild(chip);
        }
      });

      /* Scroll active tab into view */
      var activeTab = dom.tabs.querySelector('.lb-mtab--active');
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block:    'nearest',
          inline:   'center'
        });
      }
    }

    /* 2. Update progress bar */
    updateProgressBar(mo);

    /* 3. Swap milestone banner */
    updateMilestoneBanner(mo);

    /* 4. Swap featured products */
    updateFeaturedProducts(mo);

    /* 5. Push ?month= to URL without reload */
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('month', mo);
      window.history.replaceState({}, '', url.toString());
    } catch (_) {}
  }

  /* ── Progress bar ──────────────────────────────────────────── */
  function updateProgressBar(mo) {
    if (!dom.progressFill) return;
    var span = state.maxMonth - state.minMonth;
    if (span <= 0) return;
    var pct = Math.min(100, Math.max(0,
      ((mo - state.minMonth) / span) * 100
    ));
    dom.progressFill.style.width = pct.toFixed(1) + '%';
  }

  /* ── Milestone banner swap ─────────────────────────────────── */
  function updateMilestoneBanner(mo) {
    if (!dom.bannerInner) return;
    var data   = state.ageData[mo] || {};
    var skills = data.skills || [];

    var eyebrow = dom.bannerInner.querySelector('.lb-ms-banner__eyebrow');
    if (eyebrow) {
      var label = mo < 12
        ? mo + 'M'
        : (mo / 12).toFixed(2).replace(/\.00$/, '') + 'Y';
      eyebrow.textContent = label +
        (state.stageName ? ' · ' + state.stageName : '');
    }

    var chipsEl = dom.bannerInner.querySelector('.lb-ms-banner__chips');
    if (!chipsEl) return;

    if (skills.length) {
      chipsEl.innerHTML = skills.map(function (s) {
        return '<span class="lb-ms-banner__chip">' + esc(s) + '</span>';
      }).join('');
    } else {
      chipsEl.innerHTML =
        '<span class="lb-ms-banner__chip">No milestone data for Month ' +
        mo + '</span>';
    }
  }

  /* ── Featured products swap ────────────────────────────────── */
  function updateFeaturedProducts(mo) {
    if (!dom.featuredTitle || !dom.featuredWrap) return;

    var label = mo < 12
      ? mo + 'M'
      : (mo / 12).toFixed(2).replace(/\.00$/, '') + 'Y';
    dom.featuredTitle.textContent = 'Perfect for ' + label;

    var data     = state.ageData[mo] || {};
    var products = data.featuredProducts || [];

    if (!products.length) {
      dom.featuredWrap.innerHTML =
        '<p style="padding:16px;color:var(--lb-ink3);font-family:var(--lb-F);font-size:12px">' +
        'No featured products for Month ' + mo + ' yet.</p>';
      return;
    }

    dom.featuredWrap.classList.add('lb-hp-scroll--loading');
    setTimeout(function () {
      dom.featuredWrap.innerHTML = products.map(function (p, i) {
        return renderFeaturedCard(p, i + 1);
      }).join('');
      applyWishlistState();
      dom.featuredWrap.classList.remove('lb-hp-scroll--loading');
    }, 120);
  }

  /* ================================================================
     SORT — client-side, no reload
  ================================================================ */
  function bindSort() {
    if (!dom.sortSelect) return;

    dom.sortSelect.addEventListener('change', function (e) {
      var val = e.target.value;

      state.currentSort = val;
      sortProducts(val);

      /*
       * Full re-render from index 0.
       * visibleCount is set to exactly one PAGE_SIZE batch — infinite
       * scroll takes over from there.
       */
      state.visibleCount = Math.min(PAGE_SIZE, state.allProducts.length);
      renderGrid(false);
      resetInfiniteScroll();
    });
  }

  function sortProducts(sortBy) {
    var arr = state.originalProducts.slice();

    if (sortBy === 'price-ascending') {
      arr.sort(function (a, b) { return (a.price || 0) - (b.price || 0); });
    } else if (sortBy === 'price-descending') {
      arr.sort(function (a, b) { return (b.price || 0) - (a.price || 0); });
    } else if (sortBy === 'best-selling') {
      arr.sort(function (a, b) {
        return (b.reviewCount || 0) - (a.reviewCount || 0);
      });
    } else if (sortBy === 'created-descending') {
      arr.sort(function (a, b) { return (b.id || 0) - (a.id || 0); });
    }

    state.allProducts = arr;
  }

  /* ================================================================
     PRODUCT GRID RENDER
     append = false  →  wipe grid, render allProducts[0..visibleCount]
     append = true   →  append   allProducts[visibleCount-PAGE_SIZE..visibleCount]
     
     NOTE on the append slice:
     When we increment visibleCount by PAGE_SIZE before calling
     renderGrid(true), the NEW cards live at:
       [oldVisibleCount .. newVisibleCount]
     which is exactly [visibleCount - PAGE_SIZE .. visibleCount]
     AFTER the increment — but only if PAGE_SIZE cards were added.
     When the final batch is smaller (e.g. 7 remaining out of 19),
     the last increment is clamped:
       newVisibleCount = Math.min(old + PAGE_SIZE, total)   e.g. 19
       startIdx = 19 - 8 = 11  ← would re-render card 11 (already shown)
     
     FIX: we pass the explicit startIdx into renderGrid so it is always
     the old visibleCount, not (new visibleCount - PAGE_SIZE).
  ================================================================ */
  function renderGrid(append, startFrom) {
    if (!dom.pgGrid) return;

    /*
     * startFrom is only meaningful when append=true.
     * It is the old visibleCount captured before incrementing,
     * so we never re-render an already-visible card.
     */
    var startIdx = append ? (startFrom || 0) : 0;
    var toRender = state.allProducts.slice(startIdx, state.visibleCount);

    if (!append) {
      dom.pgGrid.innerHTML = '';
    }

    if (!toRender.length && !append) {
      dom.pgGrid.innerHTML =
        '<div class="lb-pg-empty" style="grid-column:1/-1">' +
        '<p>No products found.</p></div>';
      return;
    }

    var frag = document.createDocumentFragment();
    toRender.forEach(function (p) {
      var wrap      = document.createElement('div');
      wrap.innerHTML = renderProductCard(p);
      var card      = wrap.firstElementChild;
      if (card) frag.appendChild(card);
    });
    dom.pgGrid.appendChild(frag);
    applyWishlistState();

    setTimeout(function () {
      loadJudgeMe();
    }, 200);
  }

  function loadJudgeMe() {
    if (window.jdgm && window.jdgm.renderWidgets) {
      window.jdgm.renderWidgets();
    } else {
      setTimeout(loadJudgeMe, 500);
    }
  }

  /* ── Age tag formatter ─────────────────────────────────────── */
  function formatAge(age) {
    if (!age) return '';

    age = age.replace(/"/g, '');

    var rawAge   = age;
    var ageClean = age.toLowerCase().trim();
    var hasNumber = /[0-9]/.test(ageClean);

    if (hasNumber) {
      ageClean = ageClean.replace(/\s/g, '');

      if (ageClean.includes('year')) {
        var parts = ageClean.split('-');
        if (parts.length > 1) {
          var start = parts[0].replace('years', '').replace('year', '');
          var end   = parts[1].replace('years', '').replace('year', '');
          return start + ' - ' + end + ' Y';
        }
        return ageClean.replace('years', '').replace('year', '') + ' Y';
      }

      if (ageClean.includes('month')) {
        var parts = ageClean.split('-');
        if (parts.length > 1) {
          var start = parts[0].replace('months', '').replace('month', '');
          var end   = parts[1].replace('months', '').replace('month', '');
          return start + ' - ' + end + ' M';
        }
        return ageClean.replace('months', '').replace('month', '') + ' M';
      }

      return rawAge;
    }

    return rawAge;
  }

  /* ── Grid card HTML ────────────────────────────────────────── */
  function renderProductCard(p) {
    var badge = '';
    if (p.badge) {
      var cls = {
        Bestseller:   'lb-pg-badge--bs',
        New:          'lb-pg-badge--new',
        Sale:         'lb-pg-badge--sale',
        'Expert Pick':'lb-pg-badge--exp'
      }[p.badge] || 'lb-pg-badge--bs';
      badge = '<span class="lb-pg-badge ' + cls + '">' + esc(p.badge) + '</span>';
    }

    var img = p.image
      ? '<img src="' + esc(p.image) + '" alt="' + esc(p.imageAlt) +
        '" width="400" height="400" loading="lazy">'
      : '<span class="lb-pg-img__ph" aria-hidden="true">🧸</span>';

    var stars = '<div class="jdgm-widget jdgm-preview-badge" data-id="' +
      p.id + '">' + (p.jdgmBadge || '') + '</div>';

    var age = p.ageTag
      ? '<span class="lb-pg-age">' + esc(formatAge(p.ageTag)) + '</span>'
      : '';

    var skill = p.skillTag
      ? '<p class="lb-pg-skill">' + esc(p.skillTag) + '</p>'
      : '';

    var formattedPrice = (p.priceFmt && p.priceFmt.includes('₹'))
      ? p.priceFmt
      : '₹ ' + (p.price / 100).toFixed(2);

    var price = '<div class="lb-pg-price">' + esc(formattedPrice);

    if (p.hasDiscount && p.compareAt > p.price) {
      var formattedCompare = (p.compareAtFmt && p.compareAtFmt.includes('₹'))
        ? p.compareAtFmt
        : '₹ ' + (p.compareAt / 100).toFixed(2);
      price += '<span class="lb-pg-price-og">' + esc(formattedCompare) + '</span>';
    }
    price += '</div>';

    var atc = (p.available && p.variantId)
      ? '<button class="lb-pg-add" type="button" data-cart-widget' +
          ' data-variant-id="' + p.variantId + '"' +
          ' data-available="true"' +
          ' aria-label="Add ' + esc(p.title) + ' to cart">' +
          '<span class="lb-atc-text">ADD TO CART</span>' +
        '</button>'
      : '<span class="lb-pg-sold-out">Sold out</span>';

    return [
      '<article class="lb-pg-card" data-product-id="' + p.id + '" role="listitem">',
        badge,
        '<button class="lb-pg-wish" type="button" aria-label="Wishlist"' +
          ' data-wishlist="' + p.id + '">' +
          '<svg class="wishlist-icon" xmlns="http://www.w3.org/2000/svg"' +
          ' width="31" height="37" viewBox="0 0 31 37" fill="none">' +
            '<path d="M15.1893 30.4896C14.9072 30.486 14.6317 30.4031' +
            ' 14.3936 30.2503C10.3478 27.7528 7.3035 25.2825 4.81796' +
            ' 22.4599C3.39913 20.9487 2.29947 19.1617 1.58588 17.2076C0.302877' +
            ' 13.4231 1.78257 9.00175 5.03254 6.91981C6.51406 5.96118 8.28226' +
            ' 5.55982 10.0273 5.78607C11.7724 6.01231 13.383 6.85174 14.5768' +
            ' 8.15724C14.7959 8.39207 14.997 8.64046 15.1893 8.88434C15.4511' +
            ' 8.54656 15.7334 8.22531 16.0342 7.9224C16.7601 7.19934 17.6225' +
            ' 6.6312 18.5702 6.2518C19.518 5.87241 20.5315 5.68951 21.5507' +
            ' 5.71399C22.5489 5.74188 23.5312 5.97351 24.4385 6.39495C25.3458' +
            ' 6.81638 26.1593 7.41891 26.8301 8.16627C29.3604 10.9347 29.9595' +
            ' 14.2947 28.5021 17.8805C27.7343 19.682 26.6481 21.3271 25.2968' +
            ' 22.7354C22.9244 25.2904 20.2399 27.5303 17.3082 29.4012C16.9148' +
            ' 29.6632 16.508 29.916 16.1057 30.1645L15.985 30.2413C15.7475' +
            ' 30.3966 15.4721 30.4825 15.1893 30.4896Z"' +
            ' stroke="#720F11" stroke-width="2.25253"/>' +
          '</svg>' +
        '</button>',
        '<a href="' + esc(p.url) + '" class="lb-pg-img-link"' +
          ' tabindex="-1" aria-hidden="true">',
          '<div class="lb-pg-img">', img, '</div>',
        '</a>',
        '<div class="lb-pg-body">',
          age,
          '<a href="' + esc(p.url) + '" class="lb-pg-name-link">',
            '<p class="lb-pg-name">' + esc(p.title) + '</p>',
          '</a>',
          skill, stars,
          '<div class="lb-pg-foot">', price, atc, '</div>',
        '</div>',
      '</article>'
    ].join('');
  }

  /* ── Featured card HTML ────────────────────────────────────── */
  function renderFeaturedCard(p, rank) {
    var first = (rank === 1);
    var why   = first
      ? '⭐ Top pick Mo ' + state.activeMonth
      : 'Great for this age';

    var formattedPrice = (p.priceFmt && p.priceFmt.includes('₹'))
      ? p.priceFmt
      : '₹ ' + (p.price / 100).toFixed(2);
    var priceHtml = esc(formattedPrice);

    if (p.hasDiscount && p.compareAt > p.price) {
      var formattedCompare = (p.compareAtFmt && p.compareAtFmt.includes('₹'))
        ? p.compareAtFmt
        : '₹ ' + (p.compareAt / 100).toFixed(2);
      priceHtml +=
        '<span class="lb-hp-price-og">' + esc(formattedCompare) + '</span>';
    }

    var img = p.image
      ? '<img src="' + esc(p.image) + '" alt="' + esc(p.imageAlt) +
        '" width="300" height="300" loading="lazy">'
      : '<span class="lb-hp-img__ph" aria-hidden="true">🧸</span>';

    var atc = p.available
      ? '<button class="lb-hp-add" type="button" data-cart-widget' +
          ' data-variant-id="' + p.variantId + '"' +
          ' data-available="true"' +
          ' aria-label="Add ' + esc(p.title) + ' to cart">' +
          '<span class="lb-atc-text">Add to Cart</span>' +
        '</button>'
      : '<span class="lb-hp-sold-out">Sold out</span>';

    return [
      '<article class="lb-hp-card' + (first ? ' lb-hp-card--first' : '') +
        '" data-product-id="' + p.id + '" role="listitem">',
        '<span class="lb-hp-rank' + (first ? ' lb-hp-rank--gold' : '') +
          '" aria-label="Rank ' + rank + '">' + rank + '</span>',
        '<button class="lb-pg-wish" type="button" aria-label="Wishlist"' +
          ' data-wishlist="' + p.id + '">' +
          '<svg class="wishlist-icon" xmlns="http://www.w3.org/2000/svg"' +
          ' width="31" height="37" viewBox="0 0 31 37" fill="none">' +
            '<path d="M15.1893 30.4896C14.9072 30.486 14.6317 30.4031' +
            ' 14.3936 30.2503C10.3478 27.7528 7.3035 25.2825 4.81796' +
            ' 22.4599C3.39913 20.9487 2.29947 19.1617 1.58588 17.2076C0.302877' +
            ' 13.4231 1.78257 9.00175 5.03254 6.91981C6.51406 5.96118 8.28226' +
            ' 5.55982 10.0273 5.78607C11.7724 6.01231 13.383 6.85174 14.5768' +
            ' 8.15724C14.7959 8.39207 14.997 8.64046 15.1893 8.88434C15.4511' +
            ' 8.54656 15.7334 8.22531 16.0342 7.9224C16.7601 7.19934 17.6225' +
            ' 6.6312 18.5702 6.2518C19.518 5.87241 20.5315 5.68951 21.5507' +
            ' 5.71399C22.5489 5.74188 23.5312 5.97351 24.4385 6.39495C25.3458' +
            ' 6.81638 26.1593 7.41891 26.8301 8.16627C29.3604 10.9347 29.9595' +
            ' 14.2947 28.5021 17.8805C27.7343 19.682 26.6481 21.3271 25.2968' +
            ' 22.7354C22.9244 25.2904 20.2399 27.5303 17.3082 29.4012C16.9148' +
            ' 29.6632 16.508 29.916 16.1057 30.1645L15.985 30.2413C15.7475' +
            ' 30.3966 15.4721 30.4825 15.1893 30.4896Z"' +
            ' stroke="#720F11" stroke-width="2.25253"/>' +
          '</svg>' +
        '</button>',
        '<a href="' + esc(p.url) + '" class="lb-hp-img-link"' +
          ' tabindex="-1" aria-hidden="true">',
          '<div class="lb-hp-img">', img, '</div>',
        '</a>',
        '<div class="lb-hp-body">',
          '<p class="lb-hp-why">' + esc(why) + '</p>',
          '<a href="' + esc(p.url) + '" class="lb-hp-name-link">',
            '<p class="lb-hp-name">' + esc(p.title) + '</p>',
          '</a>',
          p.skillTag
            ? '<p class="lb-hp-skill">' + esc(p.skillTag) + '</p>'
            : '',
          '<div class="lb-hp-foot">',
            '<div class="lb-hp-price">' + priceHtml + '</div>',
            atc,
          '</div>',
        '</div>',
      '</article>'
    ].join('');
  }

  /* ================================================================
     INFINITE SCROLL
     ──────────────────────────────────────────────────────────────
     KEY DESIGN DECISIONS
     ────────────────────

     1. startFrom is captured BEFORE visibleCount is incremented.
        renderGrid(true, startFrom) slices [startFrom..newVisibleCount].
        This is the only safe way to avoid off-by-one duplicates when
        the final batch is smaller than PAGE_SIZE.

     2. batchInFlight flag prevents the IntersectionObserver and the
        scroll fallback from double-firing a batch simultaneously.

     3. scrollFallback is stored on state so resetInfiniteScroll can
        call removeEventListener with the exact same function reference.
        Without this, each call to bindInfiniteScroll would stack another
        anonymous listener that can never be removed.
  ================================================================ */
  function bindInfiniteScroll() {
    if (!dom.sentinel) return;

    /* No IntersectionObserver support — load everything at once */
    if (!('IntersectionObserver' in window)) {
      var remaining = state.allProducts.length - state.visibleCount;
      if (remaining > 0) {
        var startFrom          = state.visibleCount;
        state.visibleCount     = state.allProducts.length;
        renderGrid(true, startFrom);
      }
      return;
    }

    function loadNextBatch() {
      /* Guard: nothing to load, or a batch is already in progress */
      if (state.batchInFlight) return;
      if (state.visibleCount >= state.allProducts.length) {
        teardownScroll();
        return;
      }

      state.batchInFlight = true;

      /* Pause observer while batch is loading */
      if (state.scrollObserver) {
        state.scrollObserver.unobserve(dom.sentinel);
      }
      if (dom.spinner) dom.spinner.removeAttribute('hidden');

      /*
       * Capture old visibleCount NOW (before incrementing).
       * renderGrid(true, startFrom) will slice [startFrom..newVisibleCount]
       * — never overlapping with already-rendered cards.
       */
      var startFrom      = state.visibleCount;
      state.visibleCount = Math.min(
        state.visibleCount + PAGE_SIZE,
        state.allProducts.length
      );

      setTimeout(function () {
        renderGrid(true, startFrom);

        if (dom.spinner) dom.spinner.setAttribute('hidden', '');
        state.batchInFlight = false;

        if (state.visibleCount >= state.allProducts.length) {
          teardownScroll();
        } else if (state.scrollObserver) {
          /* Re-observe so the next scroll fires another batch */
          state.scrollObserver.observe(dom.sentinel);
        }
      }, 300);
    }

    /* Disconnect observer AND remove scroll fallback cleanly */
    function teardownScroll() {
      if (state.scrollObserver) {
        state.scrollObserver.disconnect();
        state.scrollObserver = null;
      }
      if (state.scrollFallback) {
        window.removeEventListener('scroll', state.scrollFallback);
        state.scrollFallback = null;
      }
    }

    state.scrollObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        loadNextBatch();
      });
    }, {
      root:       null,
      rootMargin: '0px 0px ' + SCROLL_MARGIN + 'px 0px',
      threshold:  0
    });

    state.scrollObserver.observe(dom.sentinel);

    /*
     * Scroll fallback — catches cases where the sentinel is already
     * in view on page load and the observer's initial callback is
     * skipped (small collections, certain mobile browsers, iframes).
     *
     * Stored on state.scrollFallback so resetInfiniteScroll can remove
     * it by reference before creating a new one.
     */
    state.scrollFallback = function () {
      if (state.visibleCount >= state.allProducts.length) {
        teardownScroll();
        return;
      }
      if (!dom.sentinel) return;
      var rect = dom.sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + SCROLL_MARGIN) {
        loadNextBatch();
      }
    };

    window.addEventListener('scroll', state.scrollFallback, { passive: true });
  }

  /* Disconnect observer + remove scroll listener, then re-bind fresh */
  function resetInfiniteScroll() {
    /* Tear down observer */
    if (state.scrollObserver) {
      state.scrollObserver.disconnect();
      state.scrollObserver = null;
    }
    /* Remove the stored scroll fallback by exact reference */
    if (state.scrollFallback) {
      window.removeEventListener('scroll', state.scrollFallback);
      state.scrollFallback = null;
    }
    /* Reset in-flight flag in case reset happens mid-batch */
    state.batchInFlight = false;

    bindInfiniteScroll();
  }

  /* ================================================================
     STICKY BAR HEIGHT SYNC
     Measures the actual theme header height and sets --lb-nav-h
     so the micro-bar sticks at exactly the right position.
  ================================================================ */
  function syncNavHeight() {
    var nav = document.querySelector(
      '#shopify-section-header, .site-header, header[role="banner"],' +
      ' .header-wrapper, .header'
    );
    if (!nav) return;

    function apply() {
      var h = nav.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--lb-nav-h', h + 'px');
        var bar = document.getElementById('lb-micro-bar');
        if (bar) bar.style.top = h + 'px';
      }
    }
    apply();
    window.addEventListener('resize', apply, { passive: true });
  }

  /* ================================================================
     HELPER — HTML escape
  ================================================================ */
  function esc(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#39;');
  }

  /* ================================================================
     WISHLIST
  ================================================================ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var STORAGE_KEY = 'wishlist_products';
    var wishlist    = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    var id          = btn.dataset.wishlist;

    if (wishlist.includes(id)) {
      wishlist = wishlist.filter(function (x) { return x !== id; });
    } else {
      wishlist.push(id);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
    applyWishlistState();
  });

  function applyWishlistState() {
    var STORAGE_KEY = 'wishlist_products';
    var wishlist    = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

    document.querySelectorAll('[data-wishlist]').forEach(function (btn) {
      btn.classList.toggle('active', wishlist.includes(btn.dataset.wishlist));
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    applyWishlistState();
  });

  /* ================================================================
     BOOT
  ================================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  function bindMasterFilter() {
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.lb-master-filter-btn');
    if (!btn) return;

    var selected = btn.getAttribute('data-category');

    document.querySelectorAll('.lb-master-filter-btn')
      .forEach(function(b) { b.classList.remove('active'); });

    btn.classList.add('active');

    if (selected === 'all') {
      state.allProducts = state.originalProducts.slice();
    } else {
      state.allProducts = state.originalProducts.filter(function(p) {
        if (!p.masterCategories) return false;

        return p.masterCategories.some(function(cat) {
          return cat.toLowerCase().replace(/\s+/g, '-') === selected;
        });
      });
    }

    state.visibleCount = Math.min(PAGE_SIZE, state.allProducts.length);
    renderGrid(false);
    resetInfiniteScroll();
  });
}
document.addEventListener("DOMContentLoaded", function () {
  const header = document.querySelector(".lb-age-header");

  if (!header) return;

  // create spacer
  const spacer = document.createElement("div");
  spacer.className = "lb-age-header-spacer";
  header.after(spacer);

  const triggerOffset = header.offsetTop;

  window.addEventListener("scroll", function () {
    if (window.scrollY >= triggerOffset - 96) {
      header.classList.add("is-sticky");
    } else {
      header.classList.remove("is-sticky");
    }
  });
});
})();


